import * as Blockly from "blockly";
import { DEPRECATED_BLOCKS } from "../deprecated-blocks.ts";
import {
  dyn,
  type DynamicDropdownToken,
  isDynamicDropdownToken,
  resolveDynamicDropdownOptionsForProvider,
} from "../dynamic-options.ts";
import { type BlocklyDataProvider, getBlocklyDataProvider } from "../provider.ts";
import type {
  BlocklyToolboxLabel,
  BlockProperties,
} from "./blockly-types.ts";
import {
  adjustColorBrightness,
  BLOCKLY_BLOCK_PREFIX,
  BLOCKLY_COLORS,
  BLOCKLY_DUMMY,
  PROPERTIES_FOR_ACTIONS,
  PROPERTIES_FOR_BUTTONS,
  PROPERTIES_FOR_CARDS,
  PROPERTIES_FOR_FUSION,
  PROPERTIES_FOR_OVERLAYS,
  PROPERTIES_FOR_PROPERTIES,
  PROPERTIES_FOR_QUIZ,
  PROPERTIES_FOR_RAGAI,
  PROPERTIES_FOR_STAGE,
  PROPERTIES_FOR_STYLINGS,
  PROPERTIES_FOR_TOASTR,
} from "./blockly-types.ts";
import { BlocklyHelpRegistry } from "./help-registry.ts";
import type { BlocklyParser, DefineBlockOptions } from "./parser.ts";
import type { PluginBlockFileContent } from "./plugin-types.ts";

export type BlocklyNesting = {
  forValue: string;
  isShadow: boolean;
  shadowOnly?: boolean;
  targetBlockType: string;
  targetBlockDefaults?: Record<string, unknown>;
};

export type BlocklyDropdownOptionsInput = string[] | DynamicDropdownToken;
export type BlocklyPairDropdownOptionsInput = [string, string][] | DynamicDropdownToken;

export class BlocklyHelper {
  public static ucfirst(str: string): string {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  public readonly DEPRECATED_BLOCKS: readonly string[] = DEPRECATED_BLOCKS;

  // blöcke wo {{var:text}} erlaubt ist
  public ALLOWED_BLOCKS_WITH_TEXT: string[] = [
    "DEBUG_CARD",
    "EVENTS_ON_NEW",
    "RAG_RAW",
    "DUMMY_URL",
    "GROUP",
    "COMMENT",
    "DUMMY_STRING",
    "RAGAIOBJ",
    "QUIZ_START_FROM_TEXT",
    "QUIZ_START_FROM_TEXT_FREETEXT",
  ];

  // Category context: wird vom Menu-Service gesetzt, damit alle Blöcke
  // innerhalb einer Kategorie deren Farbe und Icon erben.
  private _catColour?: string | number;
  private _catIcon?: string;
  private _defaultDefineOptions: DefineBlockOptions = {};

  public setCategoryContext(colour?: string | number, icon?: string) {
    this._catColour = colour;
    this._catIcon = icon;
  }

  public clearCategoryContext() {
    this._catColour = undefined;
    this._catIcon = undefined;
  }

  public setDefaultDefineOptions(options: DefineBlockOptions = {}): DefineBlockOptions {
    const previous = this._defaultDefineOptions;
    this._defaultDefineOptions = options;
    return previous;
  }

  public patchAllDeprecatedBlockColours() {
    for (const blockType of this.DEPRECATED_BLOCKS) {
      this.templateParser.patchBlockColour(blockType, BLOCKLY_COLORS.DEPRECATED);
    }
  }

  private readonly templateParser: BlocklyParser;
  private readonly blockHelpRegistry: BlocklyHelpRegistry;

  constructor(
    templateParser: BlocklyParser,
    blockHelpRegistry: BlocklyHelpRegistry = new BlocklyHelpRegistry(),
  ) {
    this.templateParser = templateParser;
    this.blockHelpRegistry = blockHelpRegistry;
  }
  public actionDropdown(
    uniqueBlockName: string,
    prefix: string = "",
    options: BlocklyPairDropdownOptionsInput = [],
    colour?: string | number,
  ): { kind: "block"; type: string } {
    const dropdownOptions = this.pairOptionsToDropdownDsl(options);
    const properties = { ...PROPERTIES_FOR_ACTIONS };
    if (colour !== undefined) properties["colour"] = colour;
    return this._registerTemplateBlock(
      uniqueBlockName,
      prefix + "{{value:dropdown=" + dropdownOptions + "}}",
      "{{value}}",
      properties,
    );
  }

  public eventDropdown(
    uniqueBlockName: string,
    options: BlocklyPairDropdownOptionsInput = [],
  ): { kind: "block"; type: string } {
    if (!isDynamicDropdownToken(options)) {
      const dropdownOptions = options.map(([value, text]) => {
        if (text == "") text = value;
        return text + "|" + JSON.stringify(value);
      });
      return this._registerTemplateBlock(
        uniqueBlockName,
        "@output:EVENT\n" + BLOCKLY_BLOCK_PREFIX.EVENTS + "{{value:dropdown=" + dropdownOptions + "}}",
        "{{value}}",
        { colour: BLOCKLY_COLORS.EVENTS_LIGHT },
      );
    }
    const mapOptions = (provider: BlocklyDataProvider): [string, string][] =>
      this.resolveDynamicPairOptions(options, provider).map(([value, text]) => {
        if (value === "???" && text === "???") return [text, value];
        if (text == "") text = value;
        return [text, JSON.stringify(value)];
      });
    // Blockly kürzt gemeinsame Präfixe/Suffixe nur bei statischen Optionen (FieldDropdown.trimOptions).
    // Event-Strings sind bot-unabhängig, daher wird die Kürzung hier beim Definieren nachgebildet.
    const trim = computeDropdownTrim(mapOptions(getBlocklyDataProvider()).map(([text]) => text));
    const dropdownOptions = dyn((provider) =>
      mapOptions(provider).map(([text, value]) => [trim.apply(text), value] as [string, string])
    );
    const properties = { colour: BLOCKLY_COLORS.EVENTS_LIGHT };
    return this._registerTemplateBlock(
      uniqueBlockName,
      "@output:EVENT\n" + BLOCKLY_BLOCK_PREFIX.EVENTS + trim.prefixLabel + "{{value:dropdown=" + dropdownOptions + "}}" + trim.suffixLabel,
      "{{value}}",
      properties,
    );
  }

  public propertyDropdown(
    uniqueBlockName: string,
    prefix: string = "",
    options: BlocklyPairDropdownOptionsInput = [],
    colour?: string | number,
    tooltip?: string,
  ): { kind: "block"; type: string } {
    const dropdownOptions = this.pairOptionsToDropdownDsl(options);

    const properties = { ...PROPERTIES_FOR_PROPERTIES };
    if (colour !== undefined) {
      properties["colour"] = colour;
    }
    if (tooltip) {
      properties["tooltip"] = tooltip;
    }
    return this._registerTemplateBlock(
      uniqueBlockName,
      "@output:String\n" + prefix + "{{value:dropdown=" + dropdownOptions + "}}",
      "{{value}}",
      properties,
    );
  }

  /*
    public eventListen(uniqueBlockName: string, prefix: string = "", options: [string, string][] = [], colour: string | number | undefined = undefined): { kind: 'block'; type: string } {
      const dropdownOptions = options.map(([text, value]) => (text + "|" + value));

      const properties = { ...PROPERTIES_FOR_EVENTS };
      if (colour !== undefined) {
        properties['colour'] = colour;
      }


      return this._registerTemplateBlock(uniqueBlockName,
        prefix + "Ereignis {{value:dropdown=" + dropdownOptions.join(",") + "}}\n{{action:statement[@check=ACTION]}}",
        "RAGAI.EVENTS.on({{value}}, async function(){\n {{action}}  \n});",
        properties
      );


    };
   */

  public StackedBlockContents(type: string, items: BlocklyDropdownOptionsInput, label: string = "") {
    if (!isDynamicDropdownToken(items) && items.length === 0) {
      items = ["???"]; // Default option if no options are provided
    }
    //    let realItems = items.map(i => i = type + ":" + i + "|" + i);
    let realItems = items;
    //    return this._custom("BLOCK_STACK_" + type, "@output:" + type + "\n" + "" + ":{{value:dropdown=" + items.join(",") + "}}", "'{{value}}'");
    if (!label) {
      label = BlocklyHelper.ucfirst(type);
    }
    return this._custom(
      "BLOCK_STACK_" + type,
      "@output:" + type + "\n" + label + "{{value:dropdown=" + this.optionsToDropdownDsl(realItems) + "}}",
      "'{{value}}'",
    );
  }

  public withNested(blockDefinition: { kind: "block"; type: string }, nesting: BlocklyNesting[]) {
    const inputs: Record<string, any> = {};
    nesting.forEach((nest) => {
      inputs[nest.forValue] = {
        ...(!nest.shadowOnly && {
          block: {
            fields: nest.targetBlockDefaults ?? {},
            type: nest.targetBlockType,
          },
        }),
        ...(nest.isShadow && {
          shadow: {
            fields: nest.targetBlockDefaults ?? {},
            type: nest.targetBlockType,
          },
        }),
      };
    });

    const r = { ...blockDefinition, inputs };
    if (blockDefinition.type.includes("QUIZ")) {
      //console.log(blockDefinition.type);
      // console.log(JSON.stringify(r.inputs));
    }
    return r;
  }

  public youtube(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, "Youtube: " + bstring, template, {
      ...PROPERTIES_FOR_ACTIONS,
      inputsInline: inline,
    });
  }

  public message(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, {
      ...PROPERTIES_FOR_ACTIONS,
      inputsInline: true,
      colour: BLOCKLY_COLORS.MESSAGES,
    });
  }

  public card(
    uniqueBlockName: string,
    prefix: string = "",
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    //    const realPrefix = BLOCKLY_BLOCK_PREFIX.CARDS.split("%").join(prefix);
    return this._registerTemplateBlock(uniqueBlockName, prefix + bstring, template, {
      ...PROPERTIES_FOR_CARDS,
      inputsInline: true,
      colour: BLOCKLY_COLORS.CARDS,
    });
  }

  public castTypeBlock(toType: string): { kind: "block"; type: string } {
    const name = BlocklyHelper.ucfirst(toType);
    return this._registerTemplateBlock(
      "CAST_TO_" + toType,
      "@output:" + toType + "\nals " + name + ": {{value}}",
      "{{value}}",
      {},
    );
  }

  public stage(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, {
      ...PROPERTIES_FOR_STAGE,
      inputsInline: inline,
    });
  }

  public styling(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, BLOCKLY_BLOCK_PREFIX.STYLINGS + "" + bstring, template, {
      ...PROPERTIES_FOR_STYLINGS,
      inputsInline: inline,
    });
  }

  public action(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
    icon?: string,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, {
      ...PROPERTIES_FOR_ACTIONS,
      inputsInline: inline,
      icon: icon,
    });
  }

  public fusion(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, BLOCKLY_BLOCK_PREFIX.FUSIONS + "" + bstring, template, {
      ...PROPERTIES_FOR_FUSION,
      inputsInline: inline,
    });
  }

  public overlay(uniqueBlockName: string, bstring: string, template: string): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, PROPERTIES_FOR_OVERLAYS);
  }

  public toastr(uniqueBlockName: string, bstring: string, template: string): { kind: "block"; type: string } {
    return this._registerTemplateBlock(
      uniqueBlockName,
      BLOCKLY_BLOCK_PREFIX.TOASTR + "" + bstring,
      template,
      PROPERTIES_FOR_TOASTR,
    );
  }
  public quiz(uniqueBlockName: string, bstring: string, template: string): { kind: "block"; type: string } {
    return this._registerTemplateBlock(
      uniqueBlockName,
      BLOCKLY_BLOCK_PREFIX.QUIZ + bstring,
      template,
      PROPERTIES_FOR_QUIZ,
    );
  }

  public button(uniqueBlockName: string, bstring: string, template: string): { kind: "block"; type: string } {
    return this._registerTemplateBlock(
      uniqueBlockName,
      BLOCKLY_BLOCK_PREFIX.BUTTONS + "" + bstring,
      template,
      PROPERTIES_FOR_BUTTONS,
    );
  }

  public skill(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, {
      colour: BLOCKLY_COLORS.SKILLS,
      inputsInline: inline,
    });
  }

  public textRagAi(
    uniqueBlockName: string,
    prefix: string = "🔤",
    defaultValue: string = "",
    suffix: string = "",
    outputType: string = "String",
  ): { kind: "block"; type: string } {
    if (defaultValue.trim() == "") {
      return this._custom(
        uniqueBlockName,
        "@output:" + outputType + "\n" + prefix + "{{text:text}}" + suffix,
        "{{text}}",
        { colour: BLOCKLY_COLORS.RAGAI_DARK },
      );
    } else {
      return this._custom(
        uniqueBlockName,
        "@output:" + outputType + "\n" + prefix + "{{text:text=" + defaultValue + "}}" + suffix,
        "{{text}}",
        { colour: BLOCKLY_COLORS.RAGAI_DARK },
      );
    }
  }

  public text(
    uniqueBlockName: string,
    prefix: string = "🔤",
    defaultValue: string = "",
    suffix: string = "",
    outputType: string = "String",
  ): { kind: "block"; type: string } {
    if (defaultValue.trim() == "") {
      return this._custom(
        uniqueBlockName,
        "@output:" + outputType + "\n" + prefix + "{{text:text}}" + suffix,
        "{{text}}",
        { colour: BLOCKLY_COLORS.MESSAGES_LIGHT },
      );
    } else {
      return this._custom(
        uniqueBlockName,
        "@output:" + outputType + "\n" + prefix + "{{text:text=" + defaultValue + "}}" + suffix,
        "{{text}}",
        { colour: BLOCKLY_COLORS.MESSAGES_LIGHT },
      );
    }
  }

  public ragaiObj(uniqueBlockName: string): { kind: "block"; type: string } {
    return this._custom(uniqueBlockName, "@output:RAGAIOBJ\n {{obj:json}}", "{{obj!}}", {
      colour: BLOCKLY_COLORS.CODE,
    });
  }

  public textarea(
    uniqueBlockName: string,
    prefix: string = "📝",
    defaultValue: string = "",
    suffix: string = "",
    outputType: string = "String",
  ): { kind: "block"; type: string } {
    if (defaultValue.trim() == "") {
      return this._custom(
        uniqueBlockName,
        "@output:" + outputType + "\n" + prefix + "{{text:textarea}}" + suffix,
        "{{text}}",
        { colour: BLOCKLY_COLORS.MESSAGES_LIGHT },
      );
    } else {
      return this._custom(
        uniqueBlockName,
        "@output:" + outputType + "\n" + prefix + "{{text:textarea=" + defaultValue + "}}" + suffix,
        "{{text}}",
        { colour: BLOCKLY_COLORS.MESSAGES_LIGHT },
      );
    }
  }

  public number(
    uniqueBlockName: string,
    defaultValue: number = 0,
    outputType: string = "Number",
  ): { kind: "block"; type: string } {
    return this._custom(
      uniqueBlockName,
      "@output:" + outputType + "\nZahl: {{number:number=" + defaultValue + "}}",
      "{{number}}",
      { colour: BLOCKLY_COLORS.MESSAGES_LIGHT },
    );
  }

  public boolean(uniqueBlockName: string, outputType: string = "Boolean"): { kind: "block"; type: string } {
    return this._custom(
      uniqueBlockName,
      "@output:" + outputType + "\nBoolean: {{value:dropdown=false,true}}",
      "{{value}}",
      { colour: BLOCKLY_COLORS.MESSAGES_LIGHT },
    );
  }

  public ragAi(uniqueBlockName: string, bstring: string, template: string): { kind: "block"; type: string } {
    return this._custom(uniqueBlockName, bstring, template, PROPERTIES_FOR_RAGAI);
  }

  public expertObj(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    defaultJson: string = "",
  ): { kind: "block"; type: string } {
    bstring = bstring.replace("_obj(", "(");
    return this.withNested(
      this._custom(uniqueBlockName, bstring, template, { inputsInline: true }),
      [{
        forValue: "obj",
        isShadow: true,
        targetBlockType: BLOCKLY_DUMMY.RAGAIOBJ,
        targetBlockDefaults: { obj: defaultJson },
      }],
    );
  }

  public _custom(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    props: BlockProperties = {},
    defineOptions: DefineBlockOptions = {},
  ): { kind: "block"; type: string } {
    if (uniqueBlockName.startsWith("EXPERT_")) {
      console.log("EXPERT BLOCK DETECTED: ", template);
      const service = uniqueBlockName.split("_")[1];
      props = {
        ...props,
        helpUrl: this.blockHelpRegistry.buildNewBlocklyDocsUrl(uniqueBlockName) ??
          this.blockHelpRegistry.getExpertDocsOverviewUrl(),
      };
      props.tooltip = "Expertenblock für " + service +
        ". Bitte die Dokumentation lesen bevor du diesen Block benutzt.\n\n🆔 " + uniqueBlockName;
    }

    return this._registerTemplateBlock(uniqueBlockName, bstring, template, props, defineOptions);
  }

  private optionsToDropdownDsl(options: BlocklyDropdownOptionsInput): string {
    return isDynamicDropdownToken(options) ? options.toString() : options.join(",");
  }

  private pairOptionsToDropdownDsl(options: BlocklyPairDropdownOptionsInput): string {
    return isDynamicDropdownToken(options)
      ? options.toString()
      : options.map(([text, value]) => (text + "|" + value)).join(",");
  }

  private resolveDynamicPairOptions(options: DynamicDropdownToken, provider: BlocklyDataProvider): [string, string][] {
    return resolveDynamicDropdownOptionsForProvider(options.id, provider).map(([text, value]) => [text, value]);
  }

  public _fromPlugin(
    pluginName: string,
    pluginBlock: PluginBlockFileContent,
    nestings: BlocklyNesting[],
    defineOptions: DefineBlockOptions = {},
  ): { kind: "block"; type: string } {
    let preparedBlock = pluginBlock.block;
    if (preparedBlock.startsWith("@output")) {
      preparedBlock = preparedBlock.replace(/^@output/, "@output:String\n (🧩) " + pluginName + "\n");
    } else {
      preparedBlock = "(🧩) " + pluginName + "\n" + preparedBlock;
    }

    // NESTINGGS
    //

    if (!pluginBlock.ID) {
      pluginBlock.ID = "PLUGIN_BLOCK_" + Math.random().toString(36).substring(2, 15);
    }
    if (nestings.length > 0) {
      return this.withNested(
        this._custom(pluginBlock.ID, preparedBlock, pluginBlock.code, {
          inputsInline: true,
          colour: BLOCKLY_COLORS.PLUGIN,
        }, defineOptions),
        nestings,
      );
    }
    return this._custom(pluginBlock.ID, preparedBlock, pluginBlock.code, {
      inputsInline: true,
      colour: BLOCKLY_COLORS.PLUGIN,
    }, defineOptions);
  }

  public eventOn(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    props: BlockProperties = {},
  ): { kind: "block"; type: string } {
    return this._registerTemplateBlock(uniqueBlockName, BLOCKLY_BLOCK_PREFIX.EVENTS + "" + bstring, template, props);
  }

  private _registerTemplateBlock(
    uniqueBlockName: string,
    blockTemplate: string,
    codeTemplate: string = "",
    blockProperties: BlockProperties = {},
    defineOptions: DefineBlockOptions = {},
  ): { kind: "block"; type: string } {
    const blockType = uniqueBlockName;
    blockProperties = { ...blockProperties };

    const wrongBlocks = [];

    // DIE MUSSEN MAL AN ANDERE STELLE IN EIN GLOBALES ZENTRALES "BLOCK IST SCHON OK SO  gepscuht werden, z.b. im BH.button() weil alle butons betreoffen sind
    // und auch im actions dropdwon weil alle actions betroffen sind
    const blocksDieschonoksindso = [
      "OVERLAY_ACTIONS",
      "RISE_ACTIONS",
      "QUIZ_ACTIONS",
      "VIMEO_ACTIONS",
      "LXT_ACTIONS",
      "RAGAI_BUTTON",
      "RAGAI_BUTTON_TEXT",
      "RAGAI_BUTTON_2",
      "RAGAI_BUTTON_TEXT_2",
      "STAGE_ACTIONS",
      "SKILL_ACTIONS",
      "PARENT_GET_TEXT",
      "DOC_LIST_JOINING_DOC",
      "YOUTUBE_ACTIONS",
      "BOT_WELCOME_MESSAGE",
      "STRING_JOINING_TXT",
      "GROUP",
      "COMMENT",
      "BOT_CONTROLS",
      "RAG_IF",
      "RAG_IF_ELSE",
      "RAG_WHILE",
      "RAG_FOR",
      "RAG_RAW",
      "OWN_FUNCTION_DEFINITION",
      "SKILL_JOINING",
    ];
    if (!blocksDieschonoksindso.includes(uniqueBlockName)) {
      // Error check: if blockTemplate starts with @output and codeTemplate ends with ;
      if (
        typeof blockTemplate === "string" &&
        typeof codeTemplate === "string" &&
        blockTemplate.trim().startsWith("@output") && codeTemplate.trim().endsWith(";")
      ) {
        wrongBlocks.push(uniqueBlockName + "  maybe wrong: (;) detected");
      }
      if (
        typeof blockTemplate === "string" &&
        typeof codeTemplate === "string" &&
        !blockTemplate.trim().startsWith("@output") && !codeTemplate.trim().endsWith(";")
      ) {
        wrongBlocks.push(uniqueBlockName + "  maybe wrong: NO (;) detected");
      }
      if (wrongBlocks.length > 0) {
        console.log("MAYBE WRONG BLOCK DETECTED ", wrongBlocks);
      }
    }

    if (
      blockTemplate.includes(":text") && !blockTemplate.includes(":textarea") &&
      !this.ALLOWED_BLOCKS_WITH_TEXT.includes(uniqueBlockName)
    ) {
      if (!this.DEPRECATED_BLOCKS.includes(uniqueBlockName)) {
        if (!blockTemplate.startsWith("@output:")) {
          console.warn("BlocklyHelper: :text not allowed for block", uniqueBlockName);
        }
      }
    }

    if (this.DEPRECATED_BLOCKS.includes(uniqueBlockName)) {
      blockProperties.deprecated = true;
      blockProperties.colour = BLOCKLY_COLORS.DEPRECATED;
    }

    if (!uniqueBlockName.startsWith("EXPERT_")) {
      const resolvedHelp = this.blockHelpRegistry.resolveStandardBlockHelp(
        uniqueBlockName,
        blockTemplate,
        blockProperties.tooltip,
        blockProperties.helpUrl,
      );

      blockProperties.tooltip = resolvedHelp.tooltip;
      blockProperties.helpUrl = resolvedHelp.helpUrl;

      if (
        this.DEPRECATED_BLOCKS.includes(uniqueBlockName) && blockProperties.tooltip &&
        !blockProperties.tooltip.startsWith("DEPRECATED - ")
      ) {
        blockProperties.tooltip = `DEPRECATED - ${blockProperties.tooltip}`;
      }
    } else if (blockProperties.tooltip && !blockProperties.tooltip.includes(`🆔 ${uniqueBlockName}`)) {
      blockProperties.tooltip = `${blockProperties.tooltip}\n\n🆔 ${uniqueBlockName}`;
    }

    // Category-Context: Farbe und Icon der Kategorie auf alle Blöcke anwenden
    if (this._catColour !== undefined) {
      blockProperties.colour = this._catColour;
    }
    if (uniqueBlockName === "COMMENT") {
      blockProperties.colour = BLOCKLY_COLORS.PROPERTIES;
    }
    if (this._catIcon) {
      blockProperties.icon = this._catIcon;
    }

    this.templateParser.defineBlock(blockTemplate, blockType, blockProperties, {
      ...this._defaultDefineOptions,
      ...defineOptions,
    });
    this.templateParser.defineJs(codeTemplate, blockType);

    return { kind: "block", type: blockType };
  }

  public label(text: string): BlocklyToolboxLabel {
    const label: BlocklyToolboxLabel = { kind: "label", text };
    return label;
  }

  public startBlock(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
    icon?: string,
  ): { kind: "block"; type: string } {
    // TODO: HIER KANN MAN SCHAUNEN OB DER SELBE UNIQUE SCHON EXISTIERT UND KRASSES ERROR SCHMEIßEN, WEIL SONST BLOCKS DEFINIERT WERDEN DIE SICH ÜBERLAPPEN UND DANN KOMMT
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, {
      colour: adjustColorBrightness(BLOCKLY_COLORS.CAT_START_ABLAUF, 20),

      nextStatement: "ACTION",
      inputsInline: inline,
      icon: icon,
    });
  }

  public startBlockNew(
    uniqueBlockName: string,
    bstring: string,
    template: string,
    inline: boolean = false,
    icon?: string,
  ): { kind: "block"; type: string } {
    // TODO: HIER KANN MAN SCHAUNEN OB DER SELBE UNIQUE SCHON EXISTIERT UND KRASSES ERROR SCHMEIßEN, WEIL SONST BLOCKS DEFINIERT WERDEN DIE SICH ÜBERLAPPEN UND DANN KOMMT
    return this._registerTemplateBlock(uniqueBlockName, bstring, template, {
      colour: adjustColorBrightness(BLOCKLY_COLORS.CAT_START_ABLAUF, 20),
      hat: "cap",
      connection: "bottom",
      nextStatement: "ACTION",
      inputsInline: inline,
      icon: icon,
    });
  }
}

function computeDropdownTrim(labels: string[]): { prefixLabel: string; suffixLabel: string; apply: (text: string) => string } {
  const none = { prefixLabel: "", suffixLabel: "", apply: (text: string) => text };
  const first = labels[0];
  if (labels.length < 2 || first === undefined) return none;
  const shortest = Blockly.utils.string.shortestStringLength(labels);
  const prefixLength = Blockly.utils.string.commonWordPrefix(labels, shortest);
  const suffixLength = Blockly.utils.string.commonWordSuffix(labels, shortest);
  if ((!prefixLength && !suffixLength) || shortest <= prefixLength + suffixLength) return none;
  const prefix = prefixLength ? first.substring(0, prefixLength - 1) : "";
  const suffix = suffixLength ? first.substr(1 - suffixLength) : "";
  return {
    prefixLabel: prefix ? prefix + " " : "",
    suffixLabel: suffix ? " " + suffix : "",
    apply: (text: string) =>
      text.length > prefixLength + suffixLength &&
        text.startsWith(first.substring(0, prefixLength)) &&
        text.endsWith(first.substring(first.length - suffixLength))
        ? text.substring(prefixLength, text.length - suffixLength)
        : text,
  };
}
